import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AccountTree,
  CalendarMonth,
  Clear,
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import SearchBar from '../common/SearchBar'
import apiService from '../../../../../services/ApiService'

const GCC_COUNTRIES = ['Bahrain', 'KSA', 'Kuwait', 'Oman', 'Qatar', 'UAE']

const GCC_FLAG_CODES = {
  Bahrain: 'bh',
  KSA: 'sa',
  Kuwait: 'kw',
  Oman: 'om',
  Qatar: 'qa',
  UAE: 'ae',
}

const SORT_OPTIONS = [
  { label: 'Newest First', value: 'publishDate:desc' },
  { label: 'Oldest First', value: 'publishDate:asc' },
  { divider: true },
  { label: 'Title A-Z', value: 'title:asc' },
  { label: 'Title Z-A', value: 'title:desc' },
  { divider: true },
  { label: 'Recently Scraped', value: 'undated:desc' },
  { label: 'Oldest Scraped', value: 'undated:asc' },
]

const datePickerFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.82rem',
    color: '#23252a',
    borderRadius: '8px',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#232536' },
    '&.Mui-focused fieldset': { borderColor: '#232536', borderWidth: '1.5px' },
    '&.Mui-error fieldset': { borderColor: '#e2e8f0' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#3a3e5a' },
}

const DocumentsFiltersBar = ({
  filters,
  onFilterChange,
  onFiltersChange,
  onSearch,
  onReset,
}) => {
  const selectedSources = useMemo(
    () => (Array.isArray(filters.source) ? filters.source : []),
    [filters.source],
  )
  const selectedCountries = useMemo(
    () => (Array.isArray(filters.countries) ? filters.countries : []),
    [filters.countries],
  )
  const selectedTaxTypes = useMemo(
    () => (Array.isArray(filters.taxType) ? filters.taxType : []),
    [filters.taxType],
  )
  const selectedDocTypes = useMemo(
    () => (Array.isArray(filters.documentType) ? filters.documentType : []),
    [filters.documentType],
  )

  // API-driven cascading filter options
  const [filterOptions, setFilterOptions] = useState({ sources: [], taxTypes: [], documentTypes: [] })
  const countriesKey = selectedCountries.join(',')
  const sourcesKey = selectedSources.join(',')
  const taxTypesKey = selectedTaxTypes.join(',')

  useEffect(() => {
    let cancelled = false
    const params = {}
    if (selectedCountries.length) params.countries = selectedCountries
    if (selectedSources.length) params.source = selectedSources
    if (selectedTaxTypes.length) params.taxType = selectedTaxTypes
    if (filters.seenStatus && filters.seenStatus !== 'ALL') params.seenStatus = filters.seenStatus
    if (filters.nullDates) params.nullDates = true
    apiService.getDocumentFilterOptions(params)
      .then((res) => {
        if (!cancelled) {
          setFilterOptions({
            sources: res?.sources ?? [],
            taxTypes: res?.taxTypes ?? [],
            documentTypes: res?.documentTypes ?? [],
          })
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countriesKey, sourcesKey, taxTypesKey, filters.seenStatus, filters.nullDates])

  const { sources: sourceOptions, taxTypes: taxTypeOptions, documentTypes: docTypeOptions } = filterOptions

  // Auto-clear child selections when the API narrows available options
  useEffect(() => {
    if (!sourceOptions.length) return
    const valid = selectedSources.filter((s) => sourceOptions.includes(s))
    if (valid.length !== selectedSources.length) onFilterChange('source', valid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceOptions])

  useEffect(() => {
    if (!taxTypeOptions.length) return
    const valid = selectedTaxTypes.filter((t) => taxTypeOptions.includes(t))
    if (valid.length !== selectedTaxTypes.length) onFilterChange('taxType', valid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxTypeOptions])

  useEffect(() => {
    if (!docTypeOptions.length) return
    const valid = selectedDocTypes.filter((t) => docTypeOptions.includes(t))
    if (valid.length !== selectedDocTypes.length) onFilterChange('documentType', valid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docTypeOptions])

  // Date range dialog
  const [dateRangeOpen, setDateRangeOpen] = useState(false)
  const [quickPreset, setQuickPreset] = useState(null)

  useEffect(() => {
    if (!filters.dateFrom && !filters.dateTo) setQuickPreset(null)
  }, [filters.dateFrom, filters.dateTo])

  const handleQuickPreset = (preset) => {
    const today = dayjs()
    setQuickPreset(preset)
    if (preset === 'today') {
      onFiltersChange({ dateFrom: today.startOf('day').format('YYYY-MM-DD'), dateTo: today.endOf('day').format('YYYY-MM-DD') })
    } else if (preset === '7days') {
      onFiltersChange({ dateFrom: today.subtract(6, 'day').startOf('day').format('YYYY-MM-DD'), dateTo: today.endOf('day').format('YYYY-MM-DD') })
    } else if (preset === '30days') {
      onFiltersChange({ dateFrom: today.subtract(29, 'day').startOf('day').format('YYYY-MM-DD'), dateTo: today.endOf('day').format('YYYY-MM-DD') })
    }
  }

  // When nullDates is active, reflect the undated option in the dropdown
  const currentSort = filters.nullDates
    ? `undated:${filters.sortDir}`
    : `${filters.sortBy}:${filters.sortDir}`

  const handleSortChange = (val) => {
    if (val.startsWith('undated:')) {
      const sortDir = val.split(':')[1]
      onFiltersChange({ sortBy: 'scrapedAt', sortDir, nullDates: true, dateFrom: '', dateTo: '' })
    } else {
      const [sortBy, sortDir] = val.split(':')
      onFiltersChange({ sortBy, sortDir, nullDates: false })
    }
  }

  const hasActiveFilters =
    selectedSources.length > 0 ||
    selectedCountries.length > 0 ||
    selectedTaxTypes.length > 0 ||
    selectedDocTypes.length > 0 ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.search ||
    filters.nullDates

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        {/* Search */}
        <SearchBar
          value={filters.search}
          onChange={() => { }}
          onSearch={onSearch}
          placeholder='Search documents'
          sx={{ minWidth: { xs: '100%', sm: 280 } }}
        />

        {/* Sort */}
        <FormControl size='small' sx={{ minWidth: 200 }}>
          <InputLabel id='doc-sort-label'>Sort By</InputLabel>
          <Select
            labelId='doc-sort-label'
            value={currentSort}
            label='Sort By'
            onChange={(e) => handleSortChange(e.target.value)}
          >
            {SORT_OPTIONS.map((o, i) =>
              o.divider ? (
                <Divider key={`divider-${i}`} />
              ) : (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>

        {/* Country filter */}
        <FormControl size='small' sx={{ minWidth: 160 }}>
          <InputLabel id='doc-country-filter-label'>Country</InputLabel>
          <Select
            labelId='doc-country-filter-label'
            multiple
            value={selectedCountries}
            label='Country'
            onChange={(e) => onFilterChange('countries', e.target.value)}
            renderValue={(selected) => selected.length === 0 ? 'All Countries' : selected.join(', ')}
            MenuProps={{ disableScrollLock: true }}
          >
            {GCC_COUNTRIES.map((c) => (
              <MenuItem key={c} value={c} sx={{ py: 0.5 }}>
                <Checkbox
                  checked={selectedCountries.includes(c)}
                  size='small'
                  sx={{ p: 0.5, '&.Mui-checked': { color: '#232536' } }}
                />
                {GCC_FLAG_CODES[c] && (
                  <img
                    loading='lazy'
                    width='18'
                    src={`https://flagcdn.com/w20/${GCC_FLAG_CODES[c]}.png`}
                    srcSet={`https://flagcdn.com/w40/${GCC_FLAG_CODES[c]}.png 2x`}
                    alt=''
                    style={{ marginRight: 6, flexShrink: 0, verticalAlign: 'middle' }}
                  />
                )}
                <ListItemText primary={c} primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Source filter - depends on selected Country */}
        <FormControl size='small' sx={{ minWidth: 180 }} disabled={sourceOptions.length === 0}>
          <InputLabel id='doc-source-filter-label'>Source</InputLabel>
          <Select
            labelId='doc-source-filter-label'
            multiple
            value={selectedSources}
            label='Source'
            onChange={(e) => onFilterChange('source', e.target.value)}
            MenuProps={{ disableScrollLock: true }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccountTree sx={{ fontSize: 15, color: '#64748b', flexShrink: 0 }} />
                <span>{selected.length === 1 ? selected[0] : `${selected.length} selected`}</span>
              </Box>
            )}
          >
            {sourceOptions.map((src) => (
              <MenuItem key={src} value={src} sx={{ py: 0.5 }}>
                <Checkbox
                  checked={selectedSources.includes(src)}
                  size='small'
                  sx={{ p: 0.5, '&.Mui-checked': { color: '#232536' } }}
                />
                <ListItemText primary={src} primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Tax Type filter - depends on selected Source */}
        <FormControl size='small' sx={{ minWidth: 180 }} disabled={taxTypeOptions.length === 0}>
          <InputLabel id='doc-taxtype-filter-label'>Tax Type</InputLabel>
          <Select
            labelId='doc-taxtype-filter-label'
            multiple
            value={selectedTaxTypes}
            label='Tax Type'
            onChange={(e) => onFilterChange('taxType', e.target.value)}
            MenuProps={{ disableScrollLock: true }}
            renderValue={(selected) => selected.length === 1 ? selected[0] : `${selected.length} selected`}
          >
            {taxTypeOptions.map((t) => (
              <MenuItem key={t} value={t} sx={{ py: 0.5 }}>
                <Checkbox
                  checked={selectedTaxTypes.includes(t)}
                  size='small'
                  sx={{ p: 0.5, '&.Mui-checked': { color: '#232536' } }}
                />
                <ListItemText primary={t} primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Document Type filter - depends on selected Tax Type */}
        <FormControl size='small' sx={{ minWidth: 180 }} disabled={docTypeOptions.length === 0}>
          <InputLabel id='doc-doctype-filter-label'>Document Type</InputLabel>
          <Select
            labelId='doc-doctype-filter-label'
            multiple
            value={selectedDocTypes}
            label='Document Type'
            onChange={(e) => onFilterChange('documentType', e.target.value)}
            MenuProps={{ disableScrollLock: true }}
            renderValue={(selected) => selected.length === 1 ? selected[0] : `${selected.length} selected`}
          >
            {docTypeOptions.map((t) => (
              <MenuItem key={t} value={t} sx={{ py: 0.5 }}>
                <Checkbox
                  checked={selectedDocTypes.includes(t)}
                  size='small'
                  sx={{ p: 0.5, '&.Mui-checked': { color: '#232536' } }}
                />
                <ListItemText primary={t} primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range */}
        <Button
          onClick={() => setDateRangeOpen(true)}
          startIcon={<CalendarMonth sx={{ fontSize: 17 }} />}
          endIcon={dateRangeOpen ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            border: '1px solid',
            px: 1.8,
            height: '40px',
            whiteSpace: 'nowrap',
            borderColor: filters.dateFrom || filters.dateTo ? '#23252a' : '#e2e8f0',
            color: filters.dateFrom || filters.dateTo ? 'white' : '#23252a',
            backgroundColor: filters.dateFrom || filters.dateTo ? '#23252a' : 'white',
            '&:hover': {
              backgroundColor: filters.dateFrom || filters.dateTo ? '#374151' : '#f9fafb',
              borderColor: '#23252a',
            },
          }}
        >
          Date Range
        </Button>

        {/* Clear all */}
        {hasActiveFilters && (
          <Tooltip title='Reset all filters'>
            <Button
              startIcon={<Clear sx={{ fontSize: 17 }} />}
              onClick={onReset}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                height: '40px',
                px: 1.8,
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                border: '1px solid #fca5a5',
                color: '#dc2626',
                backgroundColor: '#fff5f5',
                '&:hover': { backgroundColor: '#fee2e2', borderColor: '#dc2626' },
              }}
            >
              Clear All
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Date Range Dialog */}
      <Dialog
        open={dateRangeOpen}
        onClose={() => setDateRangeOpen(false)}
        maxWidth='xs'
        fullWidth
        disableScrollLock
        PaperProps={{ sx: { borderRadius: '12px', mx: { xs: 2, sm: 'auto' } } }}
      >
        <DialogTitle disableTypography sx={{ py: 1.5, px: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>
              Filter by Publish Date
            </Typography>
            <IconButton
              onClick={() => setDateRangeOpen(false)}
              size='small'
              sx={{ color: '#94a3b8', '&:hover': { backgroundColor: '#f1f5f9' } }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, px: 2.5, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            {/* Quick presets */}
            <Box sx={{
              width: { xs: '100%', sm: 140 },
              flexShrink: 0,
              borderRight: { xs: 'none', sm: '1px solid #e2e8f0' },
              borderBottom: { xs: '1px solid #e2e8f0', sm: 'none' },
              pb: { xs: 1.5, sm: 0 },
              mb: { xs: 0.5, sm: 0 },
              pr: { xs: 0, sm: 1.5 },
            }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', mb: 1 }}>
                Quick Select
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flexDirection: { xs: 'row', sm: 'column' } }}>
                {[
                  { label: 'Today', value: 'today' },
                  { label: 'Last 7 Days', value: '7days' },
                  { label: 'Last 30 Days', value: '30days' },
                ].map((p) => (
                  <Button
                    key={p.value}
                    onClick={() => handleQuickPreset(p.value)}
                    disableElevation
                    size='small'
                    sx={{
                      textTransform: 'none',
                      fontWeight: quickPreset === p.value ? 700 : 500,
                      fontSize: '0.82rem',
                      borderRadius: '6px',
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      backgroundColor: quickPreset === p.value ? '#23252a' : '#f1f5f9',
                      color: quickPreset === p.value ? 'white' : '#374151',
                      px: 1.5,
                      py: 0.6,
                      minWidth: 0,
                      '&:hover': { backgroundColor: quickPreset === p.value ? '#374151' : '#e2e8f0' },
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Custom range */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                Custom Range
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>From</Typography>
                  <DatePicker
                    value={filters.dateFrom ? dayjs(filters.dateFrom) : null}
                    onChange={(v) => { onFilterChange('dateFrom', v ? v.format('YYYY-MM-DD') : ''); setQuickPreset(null) }}
                    format='DD/MM/YYYY'
                    maxDate={filters.dateTo ? dayjs(filters.dateTo) : undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true, placeholder: 'DD/MM/YYYY', sx: datePickerFieldSx }, popper: { disableScrollLock: true } }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>To</Typography>
                  <DatePicker
                    value={filters.dateTo ? dayjs(filters.dateTo) : null}
                    onChange={(v) => { onFilterChange('dateTo', v ? v.format('YYYY-MM-DD') : ''); setQuickPreset(null) }}
                    format='DD/MM/YYYY'
                    minDate={filters.dateFrom ? dayjs(filters.dateFrom) : undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true, placeholder: 'DD/MM/YYYY', sx: datePickerFieldSx }, popper: { disableScrollLock: true } }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1 }}>
          <Button
            onClick={() => { onFiltersChange({ dateFrom: '', dateTo: '' }); setQuickPreset(null) }}
            size='small'
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderRadius: '6px',
              border: '1px solid #23252a',
              color: '#23252a',
              px: 2,
              '&:hover': { borderColor: '#23252a', color: '#23252a', backgroundColor: '#f1f5f9' },
            }}
          >
            Reset
          </Button>
          <Button
            onClick={() => setDateRangeOpen(false)}
            variant='contained'
            size='small'
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderRadius: '6px',
              backgroundColor: '#23252a',
              color: 'white',
              px: 2.5,
              '&:hover': { backgroundColor: '#374151' },
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Active filter chips */}
      {(selectedCountries.length > 0 || selectedSources.length > 0 || selectedTaxTypes.length > 0 || selectedDocTypes.length > 0 || filters.dateFrom || filters.dateTo || filters.nullDates) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
          {selectedCountries.map((c) => (
            <Chip key={c} label={c} size='small' onDelete={() => onFilterChange('countries', selectedCountries.filter((x) => x !== c))} sx={{ bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }} />
          ))}
          {selectedSources.map((src) => (
            <Chip key={src} label={`Source: ${src}`} size='small' onDelete={() => onFilterChange('source', selectedSources.filter((x) => x !== src))} sx={{ bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }} />
          ))}
          {selectedTaxTypes.map((t) => (
            <Chip key={t} label={`Tax: ${t}`} size='small' onDelete={() => onFilterChange('taxType', selectedTaxTypes.filter((x) => x !== t))} sx={{ bgcolor: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74' }} />
          ))}
          {selectedDocTypes.map((t) => (
            <Chip key={t} label={t} size='small' onDelete={() => onFilterChange('documentType', selectedDocTypes.filter((x) => x !== t))} sx={{ bgcolor: '#ede9fe', color: '#7c3aed', border: '1px solid #c4b5fd' }} />
          ))}
          {filters.dateFrom && (
            <Chip label={`From: ${dayjs(filters.dateFrom).format('DD/MM/YYYY')}`} size='small' onDelete={() => onFilterChange('dateFrom', '')} sx={{ bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }} />
          )}
          {filters.dateTo && (
            <Chip label={`To: ${dayjs(filters.dateTo).format('DD/MM/YYYY')}`} size='small' onDelete={() => onFilterChange('dateTo', '')} sx={{ bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }} />
          )}
          {filters.nullDates && (
            <Chip
              label='Undated docs'
              size='small'
              onDelete={() => onFiltersChange({ nullDates: false, sortBy: 'publishDate', sortDir: 'desc' })}
              sx={{ bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

export default DocumentsFiltersBar
